/**
 * DiscordChannelPicker — the headline feature of the v2.0 NewAgentWizard.
 *
 * Given a validated Discord bot token, fetches every guild + channel the
 * bot can see via `POST /channels/discord/guilds` and renders:
 *
 *   - a search box that filters both guild names and channel names
 *   - a chip row at the top showing currently-selected channels with
 *     a per-chip remove button (keyboard: Enter/Space)
 *   - one collapsible <section> per guild containing its channels as
 *     plain <input type="checkbox"> elements (we ship no Radix checkbox
 *     primitive yet, so native is the right call — accessible and free)
 *
 * Only text (type=0) and announcement (type=5) channels are offered;
 * voice, category, and thread channels are filtered out because the
 * agent Discord bot can't usefully join them for chat.
 *
 * Style brief is "Linear-density": tight rows, small icons (h-3.5/h-4),
 * border-only cards, no drop shadows, no oversized illustrations.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  Hash,
  Loader2,
  Megaphone,
  RefreshCw,
  Search,
  Server,
  X as XIcon,
} from 'lucide-react';
import {
  listDiscordGuilds,
  type DiscordChannelV2,
  type DiscordGuildV2,
} from '@/api/channels';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface DiscordChannelPickerProps {
  /** A validated Discord bot token. Required — no-op without one. */
  token: string;
  /** Currently selected channel IDs (controlled component). */
  selectedChannelIds: string[];
  /** Called on every selection change with the new list. */
  onChange: (channelIds: string[]) => void;
  /** Disables the entire picker, incl. checkboxes + chips. */
  disabled?: boolean;
}

/** Discord channel type codes we surface in the picker. */
const TEXT_CHANNEL_TYPES = new Set<number>([0, 5]);

/**
 * Known type names so we can render a correct icon. If Discord ships a
 * new code we don't know, fall back to the generic hash icon.
 */
function channelIcon(type: number) {
  if (type === 5) return Megaphone;
  return Hash;
}

function sortGuilds(guilds: DiscordGuildV2[]): DiscordGuildV2[] {
  return [...guilds]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((g) => ({
      ...g,
      channels: [...g.channels]
        .filter((c) => TEXT_CHANNEL_TYPES.has(c.type))
        .sort((a, b) => a.name.localeCompare(b.name)),
    }));
}

export function DiscordChannelPicker({
  token,
  selectedChannelIds,
  onChange,
  disabled,
}: DiscordChannelPickerProps) {
  const [filter, setFilter] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const guildsQuery = useQuery<DiscordGuildV2[]>({
    queryKey: ['discord-guilds', token],
    queryFn: () => listDiscordGuilds(token),
    enabled: !!token,
    staleTime: 60_000,
    retry: 1,
  });

  const rawGuilds = guildsQuery.data ?? [];
  const guilds = useMemo(() => sortGuilds(rawGuilds), [rawGuilds]);

  // Flatten channels once so we can render chips by ID without a search.
  const channelsById = useMemo(() => {
    const out = new Map<string, { channel: DiscordChannelV2; guild: DiscordGuildV2 }>();
    for (const g of guilds) {
      for (const c of g.channels) {
        out.set(c.id, { channel: c, guild: g });
      }
    }
    return out;
  }, [guilds]);

  const normalized = filter.trim().toLowerCase();
  const filteredGuilds = useMemo(() => {
    if (!normalized) return guilds;
    return guilds
      .map((g) => {
        const guildHit = g.name.toLowerCase().includes(normalized);
        const channels = g.channels.filter((c) =>
          c.name.toLowerCase().includes(normalized)
        );
        if (guildHit) return g; // keep all channels if guild name matches
        if (channels.length === 0) return null;
        return { ...g, channels };
      })
      .filter((g): g is DiscordGuildV2 => g !== null);
  }, [guilds, normalized]);

  // Reset collapse state whenever the guild list rotates. First load
  // leaves everything expanded; later fetches preserve the user's state.
  useEffect(() => {
    if (!guildsQuery.isSuccess) return;
    setCollapsed((prev) => {
      const next = { ...prev };
      for (const g of guilds) {
        if (!(g.id in next)) next[g.id] = false;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guildsQuery.isSuccess, guilds.length]);

  function toggleChannel(id: string) {
    if (disabled) return;
    const isOn = selectedChannelIds.includes(id);
    onChange(isOn ? selectedChannelIds.filter((c) => c !== id) : [...selectedChannelIds, id]);
  }

  function removeChannel(id: string) {
    if (disabled) return;
    onChange(selectedChannelIds.filter((c) => c !== id));
  }

  function toggleCollapsed(guildId: string) {
    setCollapsed((prev) => ({ ...prev, [guildId]: !prev[guildId] }));
  }

  // ---------- render branches

  if (!token) {
    return (
      <div className="rounded-md border border-dashed border-border px-4 py-6 text-center text-xs text-muted-foreground">
        Validate a Discord token to load channels.
      </div>
    );
  }

  if (guildsQuery.isLoading) {
    return (
      <div
        className="space-y-2 rounded-md border border-border p-3"
        role="status"
        aria-busy="true"
        aria-label="Loading Discord channels"
      >
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          Loading guilds…
        </div>
        <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
        <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (guildsQuery.isError) {
    const msg =
      guildsQuery.error instanceof ApiError
        ? guildsQuery.error.detail ?? guildsQuery.error.title
        : (guildsQuery.error as Error)?.message ?? 'Unknown error';
    return (
      <div
        className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        role="alert"
      >
        <div>Could not load Discord channels: {msg}</div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => guildsQuery.refetch()}
          disabled={guildsQuery.isFetching}
        >
          <RefreshCw className={cn('h-3.5 w-3.5', guildsQuery.isFetching && 'animate-spin')} />
          Retry
        </Button>
      </div>
    );
  }

  const totalChannels = guilds.reduce((n, g) => n + g.channels.length, 0);

  return (
    <div className="space-y-3" aria-label="Discord channel picker">
      {/* ----- Selection chips row ----- */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {selectedChannelIds.length} channel
            {selectedChannelIds.length === 1 ? '' : 's'} selected
          </span>
          <span>
            {guilds.length} guild{guilds.length === 1 ? '' : 's'} · {totalChannels} channel
            {totalChannels === 1 ? '' : 's'} visible
          </span>
        </div>
        {selectedChannelIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selectedChannelIds.map((id) => {
              const hit = channelsById.get(id);
              const label = hit ? `#${hit.channel.name}` : id;
              const guildName = hit?.guild.name;
              return (
                <span
                  key={id}
                  className="inline-flex max-w-full items-center gap-1 rounded-md border border-border bg-secondary/60 px-2 py-0.5 text-xs"
                  title={guildName ? `${guildName} · ${label}` : label}
                >
                  <Hash className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                  <span className="truncate">
                    {hit?.channel.name ?? id}
                    {guildName && (
                      <span className="ml-1 text-muted-foreground">· {guildName}</span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeChannel(id)}
                    disabled={disabled}
                    className="rounded hover:text-destructive disabled:opacity-40"
                    aria-label={`Remove ${label}`}
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* ----- Search box ----- */}
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter channels by name…"
          className="pl-8 text-xs"
          disabled={disabled}
          aria-label="Filter channels"
        />
      </div>

      {/* ----- Guild list ----- */}
      <Card>
        <CardContent className="max-h-80 overflow-y-auto p-2">
          {filteredGuilds.length === 0 ? (
            <div className="px-2 py-4 text-center text-xs text-muted-foreground">
              {normalized ? 'No channels match your filter.' : 'This bot is not in any guilds yet.'}
            </div>
          ) : (
            <ul className="space-y-1">
              {filteredGuilds.map((g) => {
                const isCollapsed = collapsed[g.id] === true;
                const Chevron = isCollapsed ? ChevronRight : ChevronDown;
                return (
                  <li key={g.id} className="rounded-md">
                    <button
                      type="button"
                      onClick={() => toggleCollapsed(g.id)}
                      className="flex w-full items-center gap-2 rounded-sm px-1.5 py-1 text-left text-xs font-semibold hover:bg-muted"
                      aria-expanded={!isCollapsed}
                      aria-controls={`guild-${g.id}-channels`}
                    >
                      <Chevron className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                      {g.icon ? (
                        <img
                          src={g.icon}
                          alt=""
                          className="h-4 w-4 rounded-sm object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <Server className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                      )}
                      <span className="truncate">{g.name}</span>
                      <span className="ml-auto text-[10px] font-normal text-muted-foreground">
                        ({g.channels.length} channel{g.channels.length === 1 ? '' : 's'})
                      </span>
                    </button>
                    {!isCollapsed && g.channels.length > 0 && (
                      <ul
                        id={`guild-${g.id}-channels`}
                        className="mt-0.5 space-y-0.5 border-l border-dashed border-border pl-5"
                      >
                        {g.channels.map((c) => {
                          const Icon = channelIcon(c.type);
                          const checked = selectedChannelIds.includes(c.id);
                          return (
                            <li key={c.id}>
                              <label
                                className={cn(
                                  'flex cursor-pointer items-center gap-2 rounded-sm px-1.5 py-1 text-xs hover:bg-muted',
                                  disabled && 'cursor-not-allowed opacity-60 hover:bg-transparent'
                                )}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleChannel(c.id)}
                                  disabled={disabled}
                                  className="h-3.5 w-3.5 accent-primary"
                                />
                                <Icon
                                  className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                                  aria-hidden
                                />
                                <span className="truncate">{c.name}</span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
